import { createContext, useContext } from "react";
import type { HTMLAttributes, KeyboardEvent, MouseEvent, ReactNode } from "react";
import { motion } from "framer-motion";

import { Checkbox } from "@/components/ui/checkbox";
import { SurfaceCard } from "@/components/system/SurfaceCard";
import { useFadeUp, useFadeUpAt, useStaggerList } from "@/components/system/motion";
import { cn } from "@/lib/utils";

/**
 * Riga densa (56–64px) e lista con stagger d'ingresso (§3/§6):
 * identità a sinistra, metadati al centro, azioni rivelate in hover.
 */

const GOLD = "hsl(var(--primary))";

/** True quando la riga è orchestrata da una `DataList` (stagger dal padre). */
const InDataList = createContext(false);

type DivProps = Omit<
  HTMLAttributes<HTMLDivElement>,
  | "title"
  | "onDrag"
  | "onDragStart"
  | "onDragEnd"
  | "onAnimationStart"
  | "onAnimationEnd"
  | "onAnimationIteration"
>;

export type DataRowDensity = "compact" | "default";

export interface DataRowProps extends DivProps {
  /** Identità: medaglione, avatar o icona. */
  leading?: ReactNode;
  /** Riga principale: nome, oggetto, titolo. */
  title: ReactNode;
  /** Riga secondaria sotto il titolo. */
  subtitle?: ReactNode;
  /** Metadati centrali (badge, date, stato) — nascosti sotto `md`. */
  meta?: ReactNode;
  /** Valore o stato sempre visibile a destra. */
  trailing?: ReactNode;
  /** Azioni: rivelate in hover su desktop, sempre visibili su touch. */
  actions?: ReactNode;
  /** Colore-dato del filo laterale (brand cliente, stato…). */
  accentColor?: string;
  /** Mostra la casella di selezione. */
  selectable?: boolean;
  selected?: boolean;
  onSelectedChange?: (selected: boolean) => void;
  /** Etichetta accessibile della casella (default: "Seleziona elemento"). */
  selectLabel?: string;
  /** Rende la riga attivabile con click/Invio/Spazio. */
  onActivate?: () => void;
  /** 56px (compact) oppure 64px (default). */
  density?: DataRowDensity;
  /** Indice per lo stagger quando la riga è usata fuori da una `DataList`. */
  index?: number;
}

export function DataRow({
  leading,
  title,
  subtitle,
  meta,
  trailing,
  actions,
  accentColor,
  selectable = false,
  selected = false,
  onSelectedChange,
  selectLabel = "Seleziona elemento",
  onActivate,
  density = "default",
  index,
  className,
  children,
  onClick,
  onKeyDown,
  ...props
}: DataRowProps) {
  const inList = useContext(InDataList);
  const standalone = !inList;
  const inheritedFade = useFadeUp();
  const standaloneFade = useFadeUpAt(index ?? 0);
  const accent = accentColor ?? GOLD;

  const handleClick = (e: MouseEvent<HTMLDivElement>) => {
    onClick?.(e);
    onActivate?.();
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    onKeyDown?.(e);
    if (!onActivate || e.defaultPrevented) return;
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      onActivate();
    }
  };

  return (
    <motion.div
      variants={standalone ? standaloneFade : inheritedFade}
      {...(standalone ? { initial: "hidden" as const, animate: "visible" as const } : {})}
      role={onActivate ? "button" : undefined}
      tabIndex={onActivate ? 0 : undefined}
      onClick={onClick || onActivate ? handleClick : undefined}
      onKeyDown={onKeyDown || onActivate ? handleKeyDown : undefined}
      className={cn(
        "group relative flex min-w-0 items-center gap-3 border-b border-white/[0.05] px-4 last:border-b-0",
        "transition-colors duration-fast ease-palazzo",
        density === "compact" ? "min-h-[56px] py-2" : "min-h-16 py-2.5",
        "hover:bg-white/[0.03]",
        onActivate &&
          "cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-inset",
        selected && "bg-primary/[0.06]",
        className,
      )}
      {...props}
    >
      {/* Filo laterale del colore-dato: acceso su selezione, accennato in hover. */}
      <span
        aria-hidden
        className={cn(
          "pointer-events-none absolute inset-y-0 left-0 w-px transition-opacity duration-base",
          selected ? "opacity-100" : "opacity-0 group-hover:opacity-60",
        )}
        style={{ background: accent }}
      />

      {selectable && (
        <span
          className="flex h-10 w-6 shrink-0 items-center justify-center"
          onClick={(e: MouseEvent) => e.stopPropagation()}
        >
          <Checkbox
            checked={selected}
            onCheckedChange={(v) => onSelectedChange?.(v === true)}
            aria-label={selectLabel}
          />
        </span>
      )}

      {leading && <span className="flex shrink-0 items-center">{leading}</span>}

      <div className="min-w-0 flex-1">
        <div className="truncate text-[13px] font-medium leading-tight text-foreground">
          {title}
        </div>
        {subtitle && (
          <div className="mt-0.5 truncate text-xs leading-tight text-muted-foreground">
            {subtitle}
          </div>
        )}
        {children}
      </div>

      {meta && (
        <div className="hidden shrink-0 items-center gap-2 md:flex">{meta}</div>
      )}

      {trailing && (
        <div className="flex shrink-0 items-center gap-2 text-right">{trailing}</div>
      )}

      {actions && (
        <div
          className={cn(
            "flex shrink-0 items-center gap-1",
            // Su touch sempre visibili, su desktop rivelate in hover/focus (§6).
            "md:opacity-0 md:transition-opacity md:duration-fast",
            "md:group-hover:opacity-100 md:group-focus-within:opacity-100",
          )}
          onClick={(e: MouseEvent) => e.stopPropagation()}
        >
          {actions}
        </div>
      )}
    </motion.div>
  );
}

export interface DataListProps extends DivProps {
  /** Avvolge la lista in una SurfaceCard L1 (default true). */
  surface?: boolean;
  /** Fascia superiore della card (titolo, conteggio, filtri). */
  header?: ReactNode;
  /** Fascia inferiore (paginazione, "e altri N" — §7). */
  footer?: ReactNode;
  /** Etichetta accessibile della lista. */
  label?: string;
  children?: ReactNode;
}

/** Contenitore di righe dense: orchestra lo stagger d'ingresso dei figli. */
export function DataList({
  surface = true,
  header,
  footer,
  label,
  className,
  children,
  ...props
}: DataListProps) {
  const container = useStaggerList();

  const list = (
    <InDataList.Provider value>
      <motion.div
        variants={container}
        initial="hidden"
        animate="visible"
        role={label ? "group" : undefined}
        aria-label={label}
        className={cn("flex min-w-0 flex-col", !surface && className)}
        {...props}
      >
        {children}
      </motion.div>
    </InDataList.Provider>
  );

  if (!surface) return list;

  return (
    <SurfaceCard
      padding="none"
      interactive={false}
      header={header}
      footer={footer}
      className={className}
    >
      {list}
    </SurfaceCard>
  );
}
